/**
 * AssistantShell — top-level container that decides whether to render the
 * channel chooser, the voice panel, or the chat panel.
 *
 * Plan reference: PR 4 (chooser), PR 5 (VoiceModePanel), PR 7 (ChatModePanel).
 *
 * VOICE-ONLY FAST PATH (Codex-locked, byte-for-byte guarantee):
 *   `AssistantShell` itself uses NO hooks beyond what `VoiceModePanel`
 *   (which is itself a thin wrapper around `VoiceBotPanel`) already
 *   used. For voice-only bots (the current production state) it
 *   returns `<VoiceModePanel>` directly with no extra effects, no
 *   `useAssistantChannels`, no localStorage read. The multi-channel
 *   path is split into `MultiChannelAssistantShell` which is only
 *   mounted when an admin explicitly enabled chat on a bot via PR 3.
 *
 * MUTEX (Codex hard rule #8): chat and voice never run concurrently.
 *   - Branches below conditionally render EITHER `VoiceModePanel` OR
 *     `ChatModePanel` — never both. React unmount-on-branch-change
 *     triggers `useChatSession` / `useVoiceSession` cleanup before
 *     the other channel mounts. The generation counter inside
 *     `useChatSession` further protects against any async tail.
 */

import React from 'react';
import { VoiceModePanel } from './VoiceModePanel';
import { ChatModePanel } from './ChatModePanel';
import { AssistantModeChooser } from './AssistantModeChooser';
import { AssistantPanelFrame } from './AssistantPanelFrame';
import { useAssistantChannels } from '../hooks/useAssistantChannels';
import { useCrossChannelMemory } from '../hooks/useCrossChannelMemory';
import { isVoiceOnly } from '../utils/channel-resolution';
import { reportWidgetInteraction } from '../utils/interaction-tracking';
import type { ChannelSettings, WidgetChannel, WidgetConfig } from '../types';
import { DEFAULT_LANGUAGE, SWITCH_TO_CHAT_LABELS, type LanguageCode } from '../config/language';

interface AssistantShellProps {
  isOpen: boolean;
  onClose: () => void;
  config: WidgetConfig;
}

const RTL_LANGUAGES: LanguageCode[] = ['he', 'ar'];

/**
 * Inner shell — mounts the channel hook + provides chooser/chat frames.
 * ONLY rendered when the bot has >1 widget channel enabled. Voice-only
 * production bots never mount this component.
 */
const MultiChannelAssistantShell: React.FC<AssistantShellProps> = ({
  isOpen,
  onClose,
  config,
}) => {
  const channels = useAssistantChannels({
    enabledChannels: config.enabledChannels,
    channelSettings: config.channelSettings as ChannelSettings | undefined,
    publicKey: config.publicKey,
  });

  // Shared conversation memory that survives voice <-> chat switches.
  // Lives at the shell level so both panels read/write the same buffer.
  // When the user switches mode mid-conversation, the new transport seeds
  // itself from this memory and the bot continues exactly where it stopped.
  const crossChannelMemory = useCrossChannelMemory();

  // Codex hard rule #8 — voice and chat must never run concurrently.
  // We enforce this purely via the conditional render branches below:
  // only ONE of VoiceModePanel / ChatModePanel is mounted at a time, and
  // React's branch-change unmount triggers the appropriate teardown
  // (data channel close, peer connection close, etc.) before the other
  // channel's hook mounts. There is therefore no need for a separate
  // shell-level tracking hook.

  const language = (config.language ?? DEFAULT_LANGUAGE) as LanguageCode;
  const dir: 'rtl' | 'ltr' = RTL_LANGUAGES.includes(language) ? 'rtl' : 'ltr';
  const panelWidth = Math.min(config.panelWidth ?? 400, 480);
  const selectChannel = (channel: WidgetChannel) => {
    channels.selectChannel(channel);
    reportWidgetInteraction(config, channel);
  };

  if (channels.showChooser) {
    return (
      <AssistantPanelFrame
        isOpen={isOpen}
        onClose={onClose}
        panelWidth={panelWidth}
        dir={dir}
      >
        <AssistantModeChooser
          availableChannels={channels.availableChannels}
          language={language}
          primaryColor={config.primaryColor}
          onSelect={selectChannel}
          onClose={onClose}
        />
      </AssistantPanelFrame>
    );
  }

  if (channels.activeChannel === 'chat') {
    return (
      <AssistantPanelFrame
        isOpen={isOpen}
        onClose={onClose}
        panelWidth={panelWidth}
        dir={dir}
      >
        <ChatModePanel
          isMounted={isOpen && !channels.showChooser}
          config={config}
          crossChannelMemory={crossChannelMemory}
          voiceAvailable={channels.availableChannels.includes('voice')}
          onSwitchToVoice={() => selectChannel('voice')}
          onClose={onClose}
        />
      </AssistantPanelFrame>
    );
  }

  // Voice path in a multi-channel bot — delegate fully to VoiceModePanel.
  // Pass onSwitchToChat so the header renders a symmetric switch button.
  // Unmount-on-channel-change triggers voice teardown (WebRTC, recording,
  // transcript flush) before ChatModePanel mounts — Codex hard rule #8.
  const chatAvailable = channels.availableChannels.includes('chat');
  return (
    <VoiceModePanel
      isOpen={isOpen}
      onClose={onClose}
      config={config}
      crossChannelMemory={crossChannelMemory}
      onSwitchToChat={chatAvailable ? () => selectChannel('chat') : undefined}
      switchToChatLabel={SWITCH_TO_CHAT_LABELS[language] ?? SWITCH_TO_CHAT_LABELS.en}
    />
  );
};

export const AssistantShell: React.FC<AssistantShellProps> = ({ isOpen, onClose, config }) => {
  // Voice-only fast path — pure synchronous check, NO hook. Production
  // voice bots take this path and render VoiceModePanel directly with
  // identical render semantics to pre-PR-4 code.
  if (isVoiceOnly(config.enabledChannels)) {
    return <VoiceModePanel isOpen={isOpen} onClose={onClose} config={config} />;
  }

  // B2 (Codex round 2): force a clean remount of the multi-channel
  // state machine whenever bot identity OR channel settings change.
  // This guarantees we re-read the scoped localStorage for the new
  // public key and never inherit `activeChannel` / `showChooser`
  // from a previous bot mounted into the same widget instance.
  const widgetSettings = (config.channelSettings as ChannelSettings | undefined)?.widget;
  const remember = widgetSettings?.rememberLastUsedChannel === true ? '1' : '0';
  const defaultChan = widgetSettings?.defaultChannel ?? '';
  const stateKey = `${config.publicKey ?? ''}|${remember}|${defaultChan}`;

  return (
    <MultiChannelAssistantShell
      key={stateKey}
      isOpen={isOpen}
      onClose={onClose}
      config={config}
    />
  );
};

export default AssistantShell;
